import { API_BASE_URL } from "../../../config";
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { CustomerPicker, ActorName, loadUserDirectory } from './TankActions';
import CustomerLink from '../../Components/CustomerLink';

const STATUS_OPTIONS = [
    { value: '', label: 'ทั้งหมด' },
    { value: 'PENDING', label: 'รอดำเนินการ' },
    { value: 'APPROVED', label: 'อนุมัติแล้ว' },
    { value: 'REJECTED', label: 'ปฏิเสธแล้ว' },
];

const statusLabel = (value) => STATUS_OPTIONS.find((o) => o.value === value)?.label || value || '-';

const PAGE_SIZE = 20;

// Phase 4 (customerData ice-tank workstream, vector 2026-07-21) — web-form request channel only
// this round; TK-push parked pending vector's mechanism decision, same as Phase 2. Approving a
// request auto-creates a real Tank server-side (see TankRequestController.approve) — this page
// doesn't build the Tank itself, just triggers it.
const TankRequestManagement = () => {
    const [requests, setRequests] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [statusFilter, setStatusFilter] = useState(STATUS_OPTIONS[1]); // default to PENDING — the actionable queue

    const [showSubmitModal, setShowSubmitModal] = useState(false);
    const [submitForm, setSubmitForm] = useState({ customerCode: '', requestedSize: '', reason: '' });
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [userDirectory, setUserDirectory] = useState({});

    const [processingId, setProcessingId] = useState(null);

    const navigate = useNavigate();

    const handleLogout = useCallback(() => {
        localStorage.removeItem('user');
        navigate('/login');
    }, [navigate]);

    const getUser = useCallback(() => {
        const raw = localStorage.getItem('user');
        if (!raw) {
            handleLogout();
            return null;
        }
        return JSON.parse(raw);
    }, [handleLogout]);

    const fetchRequests = useCallback(async () => {
        const user = getUser();
        if (!user?.token) return;

        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({ page: String(page), size: String(PAGE_SIZE) });
            if (statusFilter?.value) params.set('status', statusFilter.value);

            const response = await fetch(`${API_BASE_URL}/customer/api/tank-requests?${params.toString()}`, {
                headers: { Authorization: `Bearer ${user.token}` },
            });
            if (!response.ok) {
                if (response.status === 403) { handleLogout(); return; }
                const errorText = await response.text();
                throw new Error(errorText || 'Failed to fetch tank requests');
            }
            const data = await response.json();
            setRequests(data?.content || []);
            setTotalPages(data?.totalPages ?? 0);
            setTotalElements(data?.totalElements ?? 0);
        } catch (e) {
            console.error('Fetch tank requests error:', e);
            setError('ไม่สามารถดึงข้อมูลคำขอถังได้');
            setRequests([]);
        } finally {
            setLoading(false);
        }
    }, [getUser, handleLogout, page, statusFilter]);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    const handleStatusChange = (opt) => {
        setStatusFilter(opt || STATUS_OPTIONS[0]);
        setPage(0);
    };

    const openSubmitModal = () => {
        setSubmitError('');
        setSubmitForm({ customerCode: '', requestedSize: '', reason: '' });
        setShowSubmitModal(true);
    };

    // uid -> name for the "ดำเนินการโดย" column (id only revealed on click)
    useEffect(() => {
        let cancelled = false;
        loadUserDirectory().then((map) => { if (!cancelled) setUserDirectory(map); });
        return () => { cancelled = true; };
    }, []);

    const handleSubmitRequest = async () => {
        const user = getUser();
        if (!user?.token) return;
        if (!submitForm.customerCode.trim()) {
            setSubmitError('กรุณาเลือกลูกค้า');
            return;
        }

        setSubmitting(true);
        setSubmitError('');
        try {
            const response = await fetch(`${API_BASE_URL}/customer/api/tank-requests`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.token}` },
                body: JSON.stringify({
                    customerCode: submitForm.customerCode.trim(),
                    requestedSize: submitForm.requestedSize.trim(),
                    reason: submitForm.reason.trim(),
                }),
            });
            if (!response.ok) {
                if (response.status === 403) { handleLogout(); return; }
                const errorText = await response.text();
                const friendly = /does not match any REGULAR customer/i.test(errorText)
                    ? 'ไม่พบลูกค้านี้ในระบบ — เลือกร้านอีกครั้ง'
                    : errorText;
                throw new Error(friendly || 'ส่งคำขอไม่สำเร็จ');
            }
            setShowSubmitModal(false);
            setPage(0);
            setStatusFilter(STATUS_OPTIONS[1]); // jump to PENDING so the new request is visible
            fetchRequests();
        } catch (e) {
            console.error('Submit tank request error:', e);
            setSubmitError(e.message || 'ส่งคำขอไม่สำเร็จ');
        } finally {
            setSubmitting(false);
        }
    };

    const processRequest = async (id, action) => {
        const user = getUser();
        if (!user?.token) return;

        setProcessingId(id);
        setError('');
        try {
            const response = await fetch(`${API_BASE_URL}/customer/api/tank-requests/${id}/${action}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.token}` },
                body: JSON.stringify({ approvedBy: user.username || 'ไม่ทราบชื่อ' }),
            });
            if (!response.ok) {
                if (response.status === 403) { handleLogout(); return; }
                const errorText = await response.text();
                throw new Error(errorText || 'ดำเนินการไม่สำเร็จ');
            }
            fetchRequests();
        } catch (e) {
            console.error(`${action} tank request error:`, e);
            setError(e.message || 'ดำเนินการไม่สำเร็จ');
        } finally {
            setProcessingId(null);
        }
    };

    return (
        <div className="btc-container">
            <h1>คำขอถัง</h1>

            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '1rem', justifyContent: 'space-between' }}>
                <div style={{ minWidth: 200 }}>
                    <div style={{ color: '#6B6B6B', fontSize: '0.85rem', marginBottom: 4 }}>สถานะ</div>
                    <Select
                        options={STATUS_OPTIONS}
                        value={statusFilter}
                        onChange={handleStatusChange}
                        className="btc-selectBox-container"
                        classNamePrefix="btc-selectBox"
                        isSearchable={false}
                    />
                </div>
                <button className="btc-btn" style={{ backgroundColor: '#E60012' }} onClick={openSubmitModal}>
                    + ขอถังใหม่
                </button>
            </div>

            {error && <p className="error">{error}</p>}

            {loading ? (
                <p>Loading...</p>
            ) : (
                <>
                    <table className="btc-table">
                        <thead>
                        <tr>
                            <th>ชื่อลูกค้า</th>
                            <th>ขนาดที่ขอ</th>
                            <th>เหตุผล</th>
                            <th>สถานะ</th>
                            <th>วันที่ขอ</th>
                            <th>ดำเนินการโดย</th>
                            <th>ตัวเลือก</th>
                        </tr>
                        </thead>
                        <tbody>
                        {requests.length === 0 ? (
                            <tr>
                                <td colSpan={7}>ไม่มีคำขอถังตรงตามเงื่อนไขที่เลือก</td>
                            </tr>
                        ) : (
                            requests.map((r) => (
                                <tr key={r.id}>
                                    <td>{r.customerCode ? <CustomerLink name={r.customerName} code={r.customerCode} /> : '-'}</td>
                                    <td>{r.requestedSize || '-'}</td>
                                    <td>{r.reason || '-'}</td>
                                    <td>{statusLabel(r.status)}</td>
                                    <td>{r.requestedAt ? r.requestedAt.slice(0, 10) : '-'}</td>
                                    <td><ActorName uid={r.approvedBy} directory={userDirectory} /></td>
                                    <td>
                                        {r.status === 'PENDING' ? (
                                            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                                                <button
                                                    className="btc-btn btn-sm"
                                                    style={{ backgroundColor: '#E60012' }}
                                                    disabled={processingId === r.id}
                                                    onClick={() => processRequest(r.id, 'approve')}
                                                >
                                                    อนุมัติ
                                                </button>
                                                <button
                                                    className="btc-btn btn-sm"
                                                    style={{ backgroundColor: '#6B6B6B' }}
                                                    disabled={processingId === r.id}
                                                    onClick={() => processRequest(r.id, 'reject')}
                                                >
                                                    ปฏิเสธ
                                                </button>
                                            </div>
                                        ) : '-'}
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
                        <button
                            className="btc-btn btn-sm"
                            style={{ backgroundColor: '#6B6B6B' }}
                            disabled={page <= 0}
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                        >
                            ก่อนหน้า
                        </button>
                        <span style={{ color: '#000' }}>
                            หน้า <span style={{ color: '#E60012', fontWeight: 700 }}>{totalPages === 0 ? 0 : page + 1}</span> / {totalPages}
                            {' '}({totalElements} รายการ)
                        </span>
                        <button
                            className="btc-btn btn-sm"
                            style={{ backgroundColor: '#6B6B6B' }}
                            disabled={page >= totalPages - 1}
                            onClick={() => setPage((p) => p + 1)}
                        >
                            ถัดไป
                        </button>
                    </div>
                </>
            )}

            {showSubmitModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: '#fff', borderRadius: '10px', padding: '24px', width: '420px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                            <div style={{ fontWeight: 600, fontSize: '16px' }}>ขอถังใหม่</div>
                            <button onClick={() => setShowSubmitModal(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6B6B6B' }}>×</button>
                        </div>

                        <div style={{ marginBottom: 12 }}>
                            <div style={{ color: '#6B6B6B', fontSize: '0.85rem', marginBottom: 4 }}>ลูกค้า</div>
                            <CustomerPicker
                                value={submitForm.customerCode}
                                onChange={(v) => setSubmitForm((f) => ({ ...f, customerCode: v }))}
                            />
                        </div>

                        <div style={{ marginBottom: 12 }}>
                            <div style={{ color: '#6B6B6B', fontSize: '0.85rem', marginBottom: 4 }}>ขนาดที่ขอ</div>
                            <input
                                type="text"
                                value={submitForm.requestedSize}
                                onChange={(e) => setSubmitForm((f) => ({ ...f, requestedSize: e.target.value }))}
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', background: '#fff', color: '#212529' }}
                            />
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <div style={{ color: '#6B6B6B', fontSize: '0.85rem', marginBottom: 4 }}>เหตุผล</div>
                            <textarea
                                value={submitForm.reason}
                                onChange={(e) => setSubmitForm((f) => ({ ...f, reason: e.target.value }))}
                                rows={3}
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical', background: '#fff', color: '#212529' }}
                            />
                        </div>

                        {submitError && <p className="error" style={{ marginBottom: 12 }}>{submitError}</p>}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button
                                className="btc-btn btn-sm"
                                style={{ backgroundColor: '#6B6B6B' }}
                                onClick={() => setShowSubmitModal(false)}
                                disabled={submitting}
                            >
                                ยกเลิก
                            </button>
                            <button
                                className="btc-btn btn-sm"
                                style={{ backgroundColor: '#E60012' }}
                                onClick={handleSubmitRequest}
                                disabled={submitting}
                            >
                                {submitting ? 'กำลังส่ง...' : 'ส่งคำขอ'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TankRequestManagement;
